import { Component } from '@theme/component';
import { VariantSelectedEvent } from '@theme/events';

export default class VariantPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));

    // Reset dropdowns to placeholder
    this.querySelectorAll('select').forEach(select => {
      select.selectedIndex = 0;

      // 👇 Add listeners to handle open/close state
      select.addEventListener('mousedown', () => this.restoreAllOptionLabels(select));
      select.addEventListener('focus', () => this.restoreAllOptionLabels(select));
      select.addEventListener('blur', () => this.cleanSelectedOption(select));
    });

    // Reset swatches to none selected
    this.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
    });

    // Disable Add to Cart initially
    this.toggleAddToCart(false);

    // Start watching the Add to Cart button
    this.observeAddToCartButton();

    // Initialize swatch labels
    this.updateSwatchLabels();
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    console.log("[VariantPicker] Option changed:", event.target.value);

    if (event.target.tagName === "SELECT") {
      this.cleanSelectedOption(event.target); // strip when closed
    }

    this.updateFormVariantId();
    this.updateSwatchLabels();

    this.dispatchEvent(
      new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' })
    );
  }

  cleanSelectedOption(selectEl) {
    const selected = selectEl.options[selectEl.selectedIndex];
    if (!selected) return;

    // Ensure every option stores its full original label
    Array.from(selectEl.options).forEach(opt => {
      if (!opt.dataset.label) {
        opt.dataset.label = opt.textContent.trim();
      }
    });

    // Strip down only the selected option for closed state
    const baseLabel = selected.dataset.label.split(" – ")[0].trim();
    selected.textContent = baseLabel;

    // Restore other options fully
    Array.from(selectEl.options).forEach(opt => {
      if (opt !== selected && opt.dataset.label) {
        opt.textContent = opt.dataset.label;
      }
    });
  }

  restoreAllOptionLabels(selectEl) {
    Array.from(selectEl.options).forEach(opt => {
      if (opt.dataset.label) {
        opt.textContent = opt.dataset.label; // restore full label
      }
    });
  }

  getAddToCartButton() {
    const container =
      this.closest('add-to-cart-component') ||
      this.closest('product-form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!container) return null;

    return (
      container.querySelector('button[name="add"]') ||
      container.querySelector('.add-to-cart-button') ||
      container.querySelector('button[type="submit"]')
    );
  }

  toggleAddToCart(enabled) {
    const addToCartButton = this.getAddToCartButton();
    if (addToCartButton) {
      addToCartButton.disabled = !enabled;
      console.log(
        "[VariantPicker] Toggling Add to Cart →",
        enabled ? "ENABLED" : "DISABLED"
      );
    } else {
      console.warn("[VariantPicker] Could not find Add to Cart button!");
    }
  }

  observeAddToCartButton() {
    const button = this.getAddToCartButton();
    if (!button) return;

    const productForm =
      this.closest('add-to-cart-component') ||
      this.closest('product-form') ||
      document.querySelector('form[action*="/cart/add"]');

    const idInput = productForm?.querySelector('input[name="id"]');

    const observer = new MutationObserver(() => {
      if (idInput?.value && this.areAllGroupsSelected() && button.disabled) {
        console.log("[VariantPicker] MutationObserver → forcing ENABLED");
        button.disabled = false;
      }
    });

    observer.observe(button, { attributes: true, attributeFilter: ["disabled"] });
  }

  areAllGroupsSelected() {
    let allSelected = true;
    this.querySelectorAll('select').forEach(select => {
      if (select.value === '') allSelected = false;
    });
    this.querySelectorAll('fieldset').forEach(fieldset => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (radios.length > 0 && !fieldset.querySelector('input[type="radio"]:checked')) {
        allSelected = false;
      }
    });
    return allSelected;
  }

  updateFormVariantId() {
    const container =
      this.closest('add-to-cart-component') ||
      this.closest('product-form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!container) {
      console.warn("[VariantPicker] No container found for updateFormVariantId.");
      return;
    }

    const idInput = container.querySelector('input[name="id"]');
    if (!idInput) {
      console.warn("[VariantPicker] No hidden variant ID input found.");
      return;
    }

    const selections = [];
    this.querySelectorAll('select').forEach(select => {
      if (select.value !== '') selections.push(select.value);
    });
    this.querySelectorAll('fieldset').forEach(fieldset => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) selections.push(checked.value);
    });

    console.log("[VariantPicker] Current selections:", selections);

    const dataScript = this.querySelector('script[type="application/json"]');
    if (!dataScript) {
      console.warn("[VariantPicker] No product JSON script found.");
      return;
    }

    let data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error("[VariantPicker] Failed to parse product JSON", e);
      return;
    }

    const variants = data.variants || [];
    const matchingVariant = variants.find(v =>
      selections.every((sel, idx) => v.options[idx] === sel)
    );

    if (matchingVariant) {
      idInput.value = matchingVariant.id;
      console.log("[VariantPicker] Variant resolved →", matchingVariant.id);

      const sectionId = this.dataset.sectionId || 'main-product';
      fetch(`${this.dataset.productUrl}?variant=${matchingVariant.id}&section_id=${sectionId}`)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newPrice = doc.querySelector('[ref="priceContainer"]');
          const currentPrice = document.querySelector('[ref="priceContainer"]');
          if (newPrice && currentPrice) {
            currentPrice.innerHTML = newPrice.innerHTML;
            console.log("[VariantPicker] Price updated for variant", matchingVariant.id);
          }
        })
        .catch(err => console.error("[VariantPicker] Price update failed:", err));

      if (this.areAllGroupsSelected()) {
        this.toggleAddToCart(true);
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        this.toggleAddToCart(false);
      }
    } else {
      idInput.value = "";
      console.warn("[VariantPicker] No matching variant for selections:", selections);
      this.toggleAddToCart(false);
    }
  }

  updateSwatchLabels() {
    this.querySelectorAll('fieldset.variant-option--swatches').forEach(fieldset => {
      const legend = fieldset.querySelector('legend');
      const valueSpan = legend?.querySelector('.variant-option__swatch-value');
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (legend && valueSpan) {
        valueSpan.textContent = checked ? checked.value : "";
      }
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
